// sidebarNav.tsx
import React from "react";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import {
  Folder,
  MessageSquare,
  Notebook,
  ListTodo,
  Wrench,
  LayoutDashboard,
  Clock,
  ChevronDown,
  ChevronRight,
  Calculator,
  Check,
  ChevronsUpDown,
  Pencil,
  Users,
  Inbox,
  Trash2,
  Lock,
  ClipboardList,
  FileText,
  Camera,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { TabType } from "./workspace-context";
import {
  addProjectMember,
  createProject,
  getCurrentUser,
  getMeRbac,
  listCompanies,
  listCompanyMembers,
  listProjectMembers,
  listProjects,
  removeProjectMember,
  updateProject,
  deleteProject,
} from "@/lib/backend";
import type {
  ProjectSummary,
  CreateProjectInput,
  CompanySummary,
  CompanyMember,
  ProjectMember,
  UpdateProjectInput,
} from "@/lib/types";
import { useUser } from "@clerk/clerk-react";
import { toast } from "@/hooks/use-toast";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  buildClientOptions,
  buildLocationOptions,
  UNKNOWN_CLIENT_KEY,
  UNKNOWN_LOCATION_KEY,
} from "@/lib/projectSelection";
import { getMiscProject, isMiscProjectName } from "@/lib/miscProject";

function emitOpenTab(
  type: TabType,
  opts?: {
    forceNew?: boolean;
    projectId?: string | null;
    projectName?: string | null;
    clientName?: string | null;
    isMisc?: boolean;

    // add these
    calculatorId?: string | null;
    calculatorName?: string | null;
  },
) {
  try {
    window.dispatchEvent(
      new CustomEvent("workspace:open-tab", {
        detail: {
          type,
          forceNew: opts?.forceNew,
          projectId: opts?.projectId ?? null,
          projectName: opts?.projectName ?? null,
          clientName: opts?.clientName ?? null,
          isMisc: opts?.isMisc ?? false,
          calculatorId: opts?.calculatorId ?? null,
          calculatorName: opts?.calculatorName ?? null,
        },
      }),
    );
  } catch {}
}

const PROJECT_TITLE_MAX_CHARS = 20;
const US_STATE_CODES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];
const LAST_STATE_KEY_PREFIX = "calcera:lastProjectState:";

function normalizePrefixInput(value: string): string {
  return value.trim().toLowerCase();
}

function parseLocationKey(locationKey: string): { city: string; state: string } {
  const [city = "", state = ""] = locationKey.split("||");
  return { city, state };
}

function TruncatingProjectTitle({
  title,
  subtitle,
}: {
  title?: string | null;
  subtitle?: string | null;
}) {
  const fullTitle = (title ?? "").trim();
  const needsTruncation = fullTitle.length > PROJECT_TITLE_MAX_CHARS;
  const [expanded, setExpanded] = React.useState(false);

  const displayTitle =
    !needsTruncation || expanded
      ? fullTitle
      : `${fullTitle.slice(0, PROJECT_TITLE_MAX_CHARS)}…`;

  const handleToggle = (event: React.MouseEvent<HTMLSpanElement>) => {
    // Prevent toggling the parent collapsible when only expanding the text.
    event.stopPropagation();
    event.preventDefault();
    setExpanded((prev) => !prev);
  };

  return (
    <div className="flex items-start gap-1 min-w-0">
      <div className="flex min-w-0 flex-col leading-tight">
        <span
          className={`text-sm font-medium text-foreground ${
            needsTruncation && !expanded ? "truncate" : ""
          }`}
        >
          {displayTitle}
        </span>
        {subtitle ? (
          <span className="text-[11px] text-muted-foreground">{subtitle}</span>
        ) : null}
      </div>
      {needsTruncation ? (
        <span
          role="button"
          aria-label={expanded ? "Collapse project title" : "Expand project title"}
          tabIndex={0}
          onClick={handleToggle}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              handleToggle(event as any);
            }
          }}
          className="ml-0.5 mt-0.5 flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded text-muted-foreground hover:text-foreground"
        >
          <ChevronRight
            className={`h-3.5 w-3.5 transition-transform ${
              expanded ? "rotate-90" : "rotate-0"
            }`}
            aria-hidden="true"
          />
        </span>
      ) : null}
    </div>
  );
}

export default function SidebarNav() {
  const [isProjectsOpen, setIsProjectsOpen] = React.useState(true);
  const [isCalculatorsOpen, setIsCalculatorsOpen] = React.useState(true);
  const [projects, setProjects] = React.useState<ProjectSummary[]>([]);
  const [loadingProjects, setLoadingProjects] = React.useState(false);
  const [projectError, setProjectError] = React.useState<string | null>(null);
  const [showCreateProjectDialog, setShowCreateProjectDialog] = React.useState(false);
  const [newProjectName, setNewProjectName] = React.useState("");
  const [newProjectClient, setNewProjectClient] = React.useState("");
  const [newProjectCity, setNewProjectCity] = React.useState("");
  const [newProjectState, setNewProjectState] = React.useState("");
  const [createProjectCompanyId, setCreateProjectCompanyId] = React.useState<string>("");
  const [companies, setCompanies] = React.useState<CompanySummary[]>([]);
  const [loadingCompanies, setLoadingCompanies] = React.useState(false);
  const [companyError, setCompanyError] = React.useState<string | null>(null);
  const [companyMembers, setCompanyMembers] = React.useState<CompanyMember[]>([]);
  const [loadingCompanyMembers, setLoadingCompanyMembers] = React.useState(false);
  const [companyMembersError, setCompanyMembersError] = React.useState<string | null>(null);
  const [memberFilter, setMemberFilter] = React.useState("");
  const [memberAssignments, setMemberAssignments] = React.useState<
    Record<
      string,
      {
        // RBAC-native: project role template to bind for this project.
        // null => not in project
        project_role_template: "ProjectManagers" | "User" | null;
      }
    >
  >({});
  const [statePickerOpen, setStatePickerOpen] = React.useState(false);
  const [creatingProject, setCreatingProject] = React.useState(false);
  const [createProjectError, setCreateProjectError] = React.useState<string | null>(null);
  const [showManageProjects, setShowManageProjects] = React.useState(false);
  const [manageEdits, setManageEdits] = React.useState<Record<string, { project_name: string; client_name: string; city: string; state: string }>>({});
  const [manageError, setManageError] = React.useState<string | null>(null);
  const [savingProjects, setSavingProjects] = React.useState(false);
  const [manageStatePickerOpen, setManageStatePickerOpen] = React.useState<Record<string, boolean>>({});
  const [manageFilter, setManageFilter] = React.useState("");
  const [confirmDeleteProject, setConfirmDeleteProject] = React.useState<{ id: string; name: string } | null>(null);
  const [deletingProject, setDeletingProject] = React.useState(false);
  const [canCreateProjects, setCanCreateProjects] = React.useState(false);
  const [canManageProjects, setCanManageProjects] = React.useState(false);
  const [loadingManageAccess, setLoadingManageAccess] = React.useState(false);
  const [meUser, setMeUser] = React.useState<any | null>(null);
  const [meRbac, setMeRbac] = React.useState<any | null>(null);
  const { user, isLoaded: userLoaded, isSignedIn } = useUser();

  // Project Members dialog state
  const [showProjectMembersDialog, setShowProjectMembersDialog] = React.useState(false);
  const [membersProject, setMembersProject] = React.useState<ProjectSummary | null>(null);
  const [membersCompanyMembers, setMembersCompanyMembers] = React.useState<CompanyMember[]>([]);
  const [membersProjectMembers, setMembersProjectMembers] = React.useState<ProjectMember[]>([]);
  const [membersLoading, setMembersLoading] = React.useState(false);
  const [membersError, setMembersError] = React.useState<string | null>(null);
  const [membersSaving, setMembersSaving] = React.useState(false);
  const [membersFilter, setMembersFilter] = React.useState("");
  const [membersAssignments, setMembersAssignments] = React.useState<
    Record<
      string,
      {
        project_role_template: "ProjectManagers" | "User" | null;
      }
    >
  >({});
  const [membersInitialAssignments, setMembersInitialAssignments] = React.useState<
    Record<
      string,
      {
        project_role_template: "ProjectManagers" | "User" | null;
      }
    >
  >({});

  const userHeaders = React.useMemo(() => {
    if (!userLoaded) return undefined;
    if (!isSignedIn) return undefined;
    const id = user?.id;
    if (!id) return undefined;
    const email = user?.primaryEmailAddress?.emailAddress || undefined;
    return { id, email };
  }, [userLoaded, isSignedIn, user?.id, user?.primaryEmailAddress?.emailAddress]);

  const lastStateStorageKey = React.useMemo(
    () => (user?.id ? `${LAST_STATE_KEY_PREFIX}${user.id}` : null),
    [user?.id],
  );

  const persistLastState = React.useCallback(
    (stateCode: string) => {
      if (!lastStateStorageKey) return;
      try {
        localStorage.setItem(lastStateStorageKey, stateCode);
      } catch {
        // best-effort only
      }
    },
    [lastStateStorageKey],
  );

  React.useEffect(() => {
    if (!userHeaders || !user?.id) {
      setCanCreateProjects(false);
      setCanManageProjects(false);
      setLoadingManageAccess(false);
      setMeUser(null);
      setMeRbac(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingManageAccess(true);
      setCanCreateProjects(false);
      setCanManageProjects(false);
      try {
        const [me, rbacResp] = await Promise.all([getCurrentUser(userHeaders), getMeRbac(userHeaders)]);
        if (cancelled) return;
        setMeUser(me?.user ?? null);
        const rbac = (rbacResp as any)?.rbac ?? null;
        setMeRbac(rbac);
        const perms = Array.isArray(rbac?.permissions) ? (rbac.permissions as string[]) : [];
        setCanCreateProjects(perms.includes("projects:create"));
        setCanManageProjects(perms.includes("projects:update"));
      } catch {
        if (!cancelled) {
          setCanCreateProjects(false);
          setCanManageProjects(false);
          setMeUser(null);
          setMeRbac(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingManageAccess(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userHeaders, user?.id]);

  const canEditMembersForProject = React.useCallback(
    (proj: ProjectSummary | null): boolean => {
      if (!proj) return false;
      // Prefer RBAC-native per-project capability when available.
      return proj.capabilities?.project_memberships_manage === true;
    },
    [],
  );

  const openProjectMembers = React.useCallback(
    (proj: ProjectSummary) => {
      setMembersProject(proj);
      setMembersFilter("");
      setMembersError(null);
      setMembersCompanyMembers([]);
      setMembersProjectMembers([]);
      setMembersAssignments({});
      setMembersInitialAssignments({});
      setShowProjectMembersDialog(true);
    },
    [],
  );

  React.useEffect(() => {
    if (!showProjectMembersDialog || !membersProject) return;
    if (!userHeaders) {
      setMembersError("Sign in to view project members.");
      return;
    }
    const companyId = typeof membersProject.company_id === "string" ? membersProject.company_id : "";
    if (!companyId) {
      setMembersError("This project has no company context; members cannot be managed.");
      return;
    }
    
    // Check if user has permission to read project members
    const canReadMembers = membersProject.capabilities?.project_memberships_read === true;
    const canManageMembers = membersProject.capabilities?.project_memberships_manage === true;
    
    if (!canReadMembers && !canManageMembers) {
      setMembersError("You don't have permission to view members for this project.");
      setMembersLoading(false);
      return;
    }
    
    let cancelled = false;
    setMembersLoading(true);
    setMembersError(null);
    (async () => {
      try {
        const [company, projectMembers] = await Promise.all([
          listCompanyMembers(companyId, userHeaders),
          listProjectMembers(membersProject.id, userHeaders),
        ]);
        if (cancelled) return;

        const currentUserId = user?.id ?? "";
        const sortedCompany = (company || []).slice().sort((a, b) => {
          const aIsMe = currentUserId && a.user_id === currentUserId;
          const bIsMe = currentUserId && b.user_id === currentUserId;
          if (aIsMe !== bIsMe) return aIsMe ? -1 : 1;
          const aKey = String(a?.profile?.display_name || a?.profile?.email || "").toLowerCase();
          const bKey = String(b?.profile?.display_name || b?.profile?.email || "").toLowerCase();
          const cmp = aKey.localeCompare(bKey);
          return cmp || String(a.user_id || "").localeCompare(String(b.user_id || ""));
        });

        const byUserId = new Map<string, ProjectMember>();
        (projectMembers || []).forEach((m) => {
          if (m?.user_id) byUserId.set(m.user_id, m);
        });

        const normalizeTemplate = (pm: ProjectMember | undefined): "ProjectManagers" | "User" | null => {
          if (!pm) return null;
          const roleName = String((pm as any)?.bindings?.project_role?.name || "").trim();
          if (roleName) {
            if (roleName === "ProjectManagers" || roleName === "DeptHead") return "ProjectManagers";
            return "User";
          }
          return "User";
        };

        const nextAssignments: Record<string, { project_role_template: "ProjectManagers" | "User" | null }> = {};
        for (const cm of sortedCompany) {
          const pm = byUserId.get(cm.user_id);
          nextAssignments[cm.user_id] = { project_role_template: pm ? normalizeTemplate(pm) : null };
        }

        setMembersCompanyMembers(sortedCompany);
        setMembersProjectMembers(Array.isArray(projectMembers) ? projectMembers : []);
        setMembersAssignments(nextAssignments);
        setMembersInitialAssignments(nextAssignments);
      } catch (err: any) {
        if (!cancelled) {
          setMembersError(err?.message || "Failed to load project members.");
          setMembersCompanyMembers([]);
          setMembersProjectMembers([]);
          setMembersAssignments({});
          setMembersInitialAssignments({});
        }
      } finally {
        if (!cancelled) setMembersLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showProjectMembersDialog, membersProject, userHeaders, user?.id]);

  const filteredMembersCompanyMembers = React.useMemo(() => {
    const q = membersFilter.trim().toLowerCase();
    if (!q) return membersCompanyMembers;
    return membersCompanyMembers.filter((m) => {
      const name = String(m?.profile?.display_name || "").toLowerCase();
      const email = String(m?.profile?.email || "").toLowerCase();
      return `${name} ${email}`.includes(q);
    });
  }, [membersCompanyMembers, membersFilter]);

  const setMembersAssignmentRole = React.useCallback(
    (userId: string, template: "ProjectManagers" | "User" | null) => {
      setMembersAssignments((prev) => ({ ...prev, [userId]: { project_role_template: template } }));
    },
    [],
  );

  const handleSaveProjectMembers = React.useCallback(async () => {
    if (!membersProject) return;
    if (!userHeaders) {
      toast({ title: "Not signed in", description: "Sign in to manage project members.", variant: "destructive" });
      return;
    }
    const canEdit = canEditMembersForProject(membersProject);
    if (!canEdit) {
      toast({
        title: "Insufficient privileges",
        description: "Only project/company admins can edit members for this project.",
        variant: "destructive",
      });
      return;
    }
    setMembersSaving(true);
    setMembersError(null);
    try {
      const changes: Array<{ user_id: string; project_role_template: "ProjectManagers" | "User" }> = [];
      const removals: string[] = [];
      const allIds = new Set<string>([
        ...Object.keys(membersInitialAssignments || {}),
        ...Object.keys(membersAssignments || {}),
      ]);
      for (const userId of allIds) {
        const before = membersInitialAssignments[userId] ?? { project_role_template: null };
        const after = membersAssignments[userId] ?? { project_role_template: null };
        if (before.project_role_template && !after.project_role_template) {
          removals.push(userId);
          continue;
        }
        if (after.project_role_template) {
          const changed = before.project_role_template !== after.project_role_template;
          if (changed) {
            changes.push({ user_id: userId, project_role_template: after.project_role_template });
          }
        }
      }

      const failures: Array<{ user_id: string; message: string }> = [];
      for (const c of changes) {
        try {
          await addProjectMember(
            membersProject.id,
            { user_id: c.user_id, project_role_template: c.project_role_template },
            userHeaders,
          );
        } catch (e: any) {
          failures.push({ user_id: c.user_id, message: e?.message || "Failed to update" });
        }
      }
      for (const userId of removals) {
        try {
          await removeProjectMember(membersProject.id, userId, userHeaders);
        } catch (e: any) {
          failures.push({
            user_id: userId,
            message: e?.message || "Failed to remove (backend may not support removals yet)",
          });
        }
      }

      // Refresh members regardless so UI reflects backend truth.
      const refreshed = await listProjectMembers(membersProject.id, userHeaders);
      setMembersProjectMembers(Array.isArray(refreshed) ? refreshed : []);

      toast(
        failures.length === 0
          ? { title: "Members updated", description: "Project members were saved successfully." }
          : {
              title: "Some changes failed",
              description: `${failures.length} update(s) failed. The list was refreshed from the server.`,
              variant: "destructive",
            },
      );

      // Notify other panels (e.g., Project Tracker) to refresh derived member displays (primaries, etc.).
      try {
        window.dispatchEvent(
          new CustomEvent("workspace:project-members-updated", { detail: { projectId: membersProject.id } }),
        );
      } catch {
        // ignore
      }

      setShowProjectMembersDialog(false);
    } catch (err: any) {
      setMembersError(err?.message || "Failed to save project members.");
    } finally {
      setMembersSaving(false);
    }
  }, [
    membersProject,
    userHeaders,
    canEditMembersForProject,
    membersInitialAssignments,
    membersAssignments,
    toast,
    addProjectMember,
    removeProjectMember,
    listProjectMembers,
  ]);

  const reloadProjects = React.useCallback(async () => {
    if (!userHeaders) {
      return [] as ProjectSummary[];
    }
        const projectData = await listProjects({ include_misc: true }, userHeaders);
    return projectData;
  }, [userHeaders]);

  React.useEffect(() => {
    let cancelled = false;
    if (!userHeaders) {
      setProjects([]);
      setProjectError(null);
      setLoadingProjects(false);
      return;
    }

    setLoadingProjects(true);
    setProjectError(null);

    (async () => {
      try {
        const projectData = await reloadProjects();
        if (cancelled) return;
        setProjects(projectData);
      } catch (error) {
        console.error("[SidebarNav] failed to load projects", error);
        if (!cancelled) {
          setProjectError("Unable to load projects");
          setProjects([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingProjects(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userHeaders, reloadProjects]);

  const NO_CLIENT_KEY = "__no-client";
  const NO_LOCATION_KEY = "__no-location";
  const UNKNOWN_LOCATION_LABEL = "Unknown location";

  const projectsByClient = React.useMemo(() => {
    const map = new Map<string, Map<string, { label: string; projects: ProjectSummary[] }>>();

    projects.forEach((proj) => {
      const rawClientName = typeof proj.client_name === "string" ? proj.client_name.trim() : "";
      const rawProjectName = typeof proj.project_name === "string" ? proj.project_name.trim() : "";
      const rawCity = typeof proj.city === "string" ? proj.city.trim() : "";
      const rawState = typeof proj.state === "string" ? proj.state.trim().toUpperCase() : "";
      const normalizedClient = rawClientName.toLowerCase();
      const normalizedProject = rawProjectName.toLowerCase();

      // Fully ignore any project that is itself labeled "Misc" (any case) or has a
      // client explicitly named "Misc" – "Misc" is reserved for notes/tasks only.
      if (normalizedClient === "misc" || isMiscProjectName(normalizedProject)) {
        return;
      }

      // Treat empty / null client as part of the uncategorized bucket
      let clientKey: string;
      if (!normalizedClient) {
        clientKey = NO_CLIENT_KEY;
      } else {
        // Find any existing client bucket whose name matches case-insensitively
        let existingKey: string | undefined;
        for (const candidate of map.keys()) {
          if (candidate !== NO_CLIENT_KEY && candidate.toLowerCase() === normalizedClient) {
            existingKey = candidate;
            break;
          }
        }
        clientKey = existingKey ?? rawClientName;
      }

      const locationKeyBase = `${rawCity.toLowerCase()}|${rawState}`;
      const locationKey = rawCity || rawState ? locationKeyBase : NO_LOCATION_KEY;
      const locationLabel =
        rawCity && rawState
          ? `${rawCity}, ${rawState}`
          : rawState
            ? rawState
            : rawCity
              ? rawCity
              : UNKNOWN_LOCATION_LABEL;

      const clientMap = map.get(clientKey) ?? new Map<string, { label: string; projects: ProjectSummary[] }>();
      const existingLocation = clientMap.get(locationKey);
      if (existingLocation) {
        existingLocation.projects.push(proj);
      } else {
        clientMap.set(locationKey, { label: locationLabel, projects: [proj] });
      }
      map.set(clientKey, clientMap);
    });

    // Sort projects within each location bucket
    for (const [, locationMap] of map.entries()) {
      for (const [locKey, bucket] of locationMap.entries()) {
        const sorted = bucket.projects
          .slice()
          .sort((a, b) => (a.project_name || "").localeCompare(b.project_name || ""));
        locationMap.set(locKey, { ...bucket, projects: sorted });
      }
    }

    return map;
  }, [projects]);

  const clientKeys = React.useMemo(() => {
    const keys = Array.from(projectsByClient.keys());
    keys.sort((a, b) => {
      if (a === NO_CLIENT_KEY && b === NO_CLIENT_KEY) return 0;
      if (a === NO_CLIENT_KEY) return 1;
      if (b === NO_CLIENT_KEY) return -1;
      return a.localeCompare(b);
    });
    return keys;
  }, [projectsByClient]);

  const miscProject = React.useMemo(
    () => getMiscProject(projects),
    [projects],
  );

  const hasProjectData = React.useMemo(() => projects.length > 0, [projects]);
  const editableProjects = React.useMemo(
    () =>
      projects.filter((p) => {
        return p.capabilities?.projects_update === true;
      }),
    [projects],
  );
  const editableProjectMap = React.useMemo(() => {
    const map = new Map<string, ProjectSummary>();
    editableProjects.forEach((p) => map.set(p.id, p));
    return map;
  }, [editableProjects]);
  const filteredManageProjects = React.useMemo(() => {
    const q = manageFilter.trim().toLowerCase();
    if (!q) return editableProjects;
    return editableProjects.filter((p) => {
      const hay = `${p.project_name || ""} ${p.client_name || ""} ${p.city || ""} ${p.state || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [editableProjects, manageFilter]);

  const knownClientOptions = React.useMemo(
    () =>
      buildClientOptions(projects).filter(
        (option) => option.key !== UNKNOWN_CLIENT_KEY && option.label.trim().length > 0,
      ),
    [projects],
  );

  const newProjectClientKey = React.useMemo(() => {
    const query = normalizePrefixInput(newProjectClient);
    if (!query) return "";
    const exact = knownClientOptions.find((option) => normalizePrefixInput(option.label) === query);
    return exact?.key ?? "";
  }, [knownClientOptions, newProjectClient]);

  const clientSuggestions = React.useMemo(() => {
    const query = normalizePrefixInput(newProjectClient);
    if (!query) return [];
    return knownClientOptions.filter((option) => normalizePrefixInput(option.label).startsWith(query)).slice(0, 20);
  }, [knownClientOptions, newProjectClient]);

  const shouldShowClientSuggestions = React.useMemo(() => {
    const query = normalizePrefixInput(newProjectClient);
    if (!query || clientSuggestions.length === 0) return false;
    return !knownClientOptions.some((option) => normalizePrefixInput(option.label) === query);
  }, [clientSuggestions, knownClientOptions, newProjectClient]);

  const locationSourceClientKeys = React.useMemo(() => {
    if (newProjectClientKey) return [newProjectClientKey];
    const clientQuery = normalizePrefixInput(newProjectClient);
    if (!clientQuery) return knownClientOptions.map((option) => option.key);
    const prefixMatches = knownClientOptions
      .filter((option) => normalizePrefixInput(option.label).startsWith(clientQuery))
      .map((option) => option.key);
    return prefixMatches.length > 0 ? prefixMatches : knownClientOptions.map((option) => option.key);
  }, [knownClientOptions, newProjectClient, newProjectClientKey]);

  const knownLocationSuggestions = React.useMemo(() => {
    const deduped = new Map<string, { key: string; label: string; city: string; state: string }>();
    locationSourceClientKeys.forEach((clientKey) => {
      const locations = buildLocationOptions(projects, clientKey);
      locations.forEach((location) => {
        if (location.key === UNKNOWN_LOCATION_KEY) return;
        if (deduped.has(location.key)) return;
        const parsed = parseLocationKey(location.key);
        if (!parsed.city && !parsed.state) return;
        deduped.set(location.key, {
          key: location.key,
          label: location.label,
          city: parsed.city,
          state: parsed.state,
        });
      });
    });
    return Array.from(deduped.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [locationSourceClientKeys, projects]);

  const locationSuggestions = React.useMemo(() => {
    const cityQuery = normalizePrefixInput(newProjectCity);
    const stateQuery = normalizePrefixInput(newProjectState);
    const labelQuery = cityQuery && stateQuery ? `${cityQuery}, ${stateQuery}` : cityQuery || stateQuery;
    if (!labelQuery) return [];
    return knownLocationSuggestions
      .filter((location) => {
        const labelMatches = location.label.toLowerCase().startsWith(labelQuery);
        if (labelMatches) return true;
        const cityMatches = cityQuery ? location.city.toLowerCase().startsWith(cityQuery) : true;
        const stateMatches = stateQuery ? location.state.toLowerCase().startsWith(stateQuery) : true;
        return cityMatches && stateMatches;
      })
      .slice(0, 20);
  }, [knownLocationSuggestions, newProjectCity, newProjectState]);

  const shouldShowLocationSuggestions = React.useMemo(() => {
    const cityQuery = normalizePrefixInput(newProjectCity);
    const stateQuery = normalizePrefixInput(newProjectState);
    if ((!cityQuery && !stateQuery) || locationSuggestions.length === 0) return false;
    return !locationSuggestions.some(
      (location) =>
        location.city.toUpperCase() === newProjectCity.trim().toUpperCase() &&
        location.state.toUpperCase() === newProjectState.trim().toUpperCase(),
    );
  }, [locationSuggestions, newProjectCity, newProjectState]);

  const applyClientSuggestion = React.useCallback((clientLabel: string) => {
    setNewProjectClient(clientLabel);
    setCreateProjectError(null);
  }, []);

  const applyLocationSuggestion = React.useCallback(
    (location: { city: string; state: string }) => {
      setNewProjectCity(location.city);
      setNewProjectState(location.state);
      if (location.state) {
        persistLastState(location.state);
      }
      setCreateProjectError(null);
    },
    [persistLastState],
  );

  const resolvedCompany = React.useMemo(() => {
    if (createProjectCompanyId) {
      const fromId = companies.find((c) => c.id === createProjectCompanyId);
      if (fromId) return fromId;
    }
    return companies[0] ?? null;
  }, [companies, createProjectCompanyId]);

  const resolvedCompanyName = resolvedCompany?.name ?? "";
  const hasCompany = Boolean(resolvedCompany?.id);
  const resolvedCompanyId = resolvedCompany?.id ?? "";

  const creatorUserId = user?.id ?? "";

  const resetCreateProjectForm = React.useCallback(() => {
    setShowCreateProjectDialog(false);
    setNewProjectName("");
    setNewProjectClient("");
    setNewProjectCity("");
    setNewProjectState("");
    setCreateProjectCompanyId("");
    setMemberFilter("");
    setMemberAssignments({});
    setStatePickerOpen(false);
    setCreateProjectError(null);
    setCompanyError(null);
    setCompanyMembersError(null);
  }, []);

  React.useEffect(() => {
    if (!showCreateProjectDialog || !userHeaders) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingCompanies(true);
        setCompanyError(null);
        setCreateProjectError(null);
        const me = await getCurrentUser(userHeaders);
        if (cancelled) return;
        const preferredCompanyId =
          typeof me?.user?.company_id === "string" ? me.user.company_id : "";
        // New /me shape does not include company_memberships; rely on GET /companies.
        const list = await listCompanies(userHeaders);
        if (cancelled) return;
        setCompanies(list);
        if (list.length === 0) {
          setCreateProjectCompanyId("");
          setCreateProjectError("No company membership found.");
          return;
        }
        if (preferredCompanyId && list.some((c) => c.id === preferredCompanyId)) {
          setCreateProjectCompanyId(preferredCompanyId);
          return;
        }
        if (!createProjectCompanyId) {
          setCreateProjectCompanyId(list[0].id);
        }
      } catch (err: any) {
        if (!cancelled) setCompanyError(err?.message || "Failed to load companies");
      } finally {
        if (!cancelled) setLoadingCompanies(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showCreateProjectDialog, userHeaders, createProjectCompanyId]);

  React.useEffect(() => {
    if (!showCreateProjectDialog || !userHeaders) return;
    if (!createProjectCompanyId) {
      setCompanyMembers([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingCompanyMembers(true);
        setCompanyMembersError(null);
        const members = await listCompanyMembers(createProjectCompanyId, userHeaders);
        if (cancelled) return;
        // Sort with creator on top, then by name/email for stability.
        const creatorId = user?.id ?? "";
        const sorted = members.slice().sort((a, b) => {
          const aIsCreator = a.user_id === creatorId;
          const bIsCreator = b.user_id === creatorId;
          if (aIsCreator !== bIsCreator) return aIsCreator ? -1 : 1;
          const aKey = (a?.profile?.display_name || a?.profile?.email || "").toLowerCase();
          const bKey = (b?.profile?.display_name || b?.profile?.email || "").toLowerCase();
          const cmp = aKey.localeCompare(bKey);
          if (cmp) return cmp;
          // Tiebreaker for stable ordering without exposing user ids in UI.
          return (a.user_id || "").localeCompare(b.user_id || "");
        });
        // Ensure creator is always visible at the top even if the members endpoint omits them.
        if (creatorId && !sorted.some((m) => m.user_id === creatorId)) {
          sorted.unshift({
            user_id: creatorId,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            profile: {
              email: user?.primaryEmailAddress?.emailAddress ?? null,
              display_name: (user?.fullName ?? "").trim() || null,
            },
          });
        }
        setCompanyMembers(sorted);
        // Initialize assignments for any new members; preserve existing.
        setMemberAssignments((prev) => {
          const next: typeof prev = { ...prev };
          for (const m of sorted) {
            if (!next[m.user_id]) {
              next[m.user_id] = { project_role_template: null };
            }
          }
          // Ensure creator defaults to a project manager role.
          if (creatorId) {
            next[creatorId] = next[creatorId] ?? { project_role_template: "ProjectManagers" };
            if (!next[creatorId].project_role_template) {
              next[creatorId].project_role_template = "ProjectManagers";
            }
          }
          return next;
        });
      } catch (err: any) {
        if (!cancelled) setCompanyMembersError(err?.message || "Failed to load members");
      } finally {
        if (!cancelled) setLoadingCompanyMembers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showCreateProjectDialog, createProjectCompanyId, userHeaders, user?.id]);

  React.useEffect(() => {
    if (!showCreateProjectDialog || !lastStateStorageKey) return;
    if (newProjectState.trim()) return;
    try {
      const stored = localStorage.getItem(lastStateStorageKey);
      if (stored && US_STATE_CODES.includes(stored)) {
        setNewProjectState(stored);
      }
    } catch {
      // ignore storage errors
    }
  }, [showCreateProjectDialog, lastStateStorageKey, newProjectState]);

  React.useEffect(() => {
    if (!showManageProjects) return;
    const next: Record<string, { project_name: string; client_name: string; city: string; state: string }> = {};
    editableProjects.forEach((p) => {
      next[p.id] = {
        project_name: p.project_name || "",
        client_name: p.client_name || "",
        city: p.city || "",
        state: p.state || "",
      };
    });
    setManageEdits(next);
    setManageError(null);
    setManageStatePickerOpen({});
  }, [showManageProjects, editableProjects]);

  const setAssignmentRole = React.useCallback(
    (userId: string, template: "ProjectManagers" | "User" | null) => {
      setMemberAssignments((prev) => {
        const next = { ...prev };
        if (template === null && userId === creatorUserId) {
          next[userId] = { project_role_template: "ProjectManagers" };
          return next;
        }
        next[userId] = { project_role_template: template };
        return next;
      });
    },
    [creatorUserId],
  );

  const filteredCompanyMembers = React.useMemo(() => {
    const q = memberFilter.trim().toLowerCase();
    if (!q) return companyMembers;
    return companyMembers.filter((m) => {
      const name = (m?.profile?.display_name || "").toLowerCase();
      const email = (m?.profile?.email || "").toLowerCase();
      return `${name} ${email}`.includes(q);
    });
  }, [companyMembers, memberFilter]);

  const handleManageFieldChange = React.useCallback(
    (projectId: string, field: "project_name" | "client_name" | "city" | "state", value: string) => {
      setManageEdits((prev) => {
        const current = prev[projectId] ?? { project_name: "", client_name: "", city: "", state: "" };
        return {
          ...prev,
          [projectId]: {
            ...current,
            [field]: value,
          },
        };
      });
    },
    [],
  );

  const handleSaveManageProjects = React.useCallback(async () => {
    if (!userHeaders) {
      toast({ title: "Not signed in", description: "Sign in to edit projects.", variant: "destructive" });
      return;
    }
    if (editableProjects.length === 0) {
      setManageError("You don't have admin access to any projects.");
      return;
    }

    setSavingProjects(true);
    setManageError(null);
    try {
      // Track local updates so we can optimistically refresh nav/grouping (client/location buckets)
      const updatedFieldsById: Record<
        string,
        { project_name: string; client_name: string; city: string; state: string }
      > = {};

      for (const proj of editableProjects) {
        const edits = manageEdits[proj.id] ?? { project_name: "", client_name: "", city: "", state: "" };
        const nextName = edits.project_name.trim();
        const nextClient = edits.client_name.trim();
        const nextCity = edits.city.trim();
        const nextState = edits.state.trim().toUpperCase();

        if (!nextName) {
          throw new Error(`Project name is required for "${proj.project_name || "project"}".`);
        }
        if (!nextClient) {
          throw new Error(`Client is required for "${proj.project_name || "project"}".`);
        }
        if (!nextState || !US_STATE_CODES.includes(nextState)) {
          throw new Error(`State must be a two-letter code for "${proj.project_name || "project"}".`);
        }

        const originalCity = (proj.city || "").trim().toUpperCase();
        const originalState = (proj.state || "").trim().toUpperCase();

        const hasNameChange = nextName !== (proj.project_name || "");
        const hasClientChange = nextClient !== (proj.client_name || "");
        const hasCityChange = nextCity.toUpperCase() !== originalCity;
        const hasStateChange = nextState !== originalState;

        if (hasNameChange || hasClientChange || hasCityChange || hasStateChange) {
          // Backend update endpoint expects core fields to be present; include name/client
          // even when unchanged so city/state-only edits are accepted.
          const payload: UpdateProjectInput = {
            project_name: nextName,
            client_name: nextClient,
          };
          // Always send city/state when any edit occurs so backend receives full location info.
          payload.city = nextCity || null;
          payload.state = nextState;
          await updateProject(proj.id, payload, userHeaders);

          updatedFieldsById[proj.id] = {
            project_name: nextName,
            client_name: nextClient,
            city: nextCity,
            state: nextState,
          };
        }
      }

      // Optimistically update projects list so location/client folders refresh immediately
      if (Object.keys(updatedFieldsById).length > 0) {
        setProjects((prev) =>
          prev.map((p) => {
            const patch = updatedFieldsById[p.id];
            return patch
              ? {
                  ...p,
                  project_name: patch.project_name,
                  client_name: patch.client_name,
                  city: patch.city || null,
                  state: patch.state || null,
                }
              : p;
          }),
        );
      }

      const refreshed = await reloadProjects();
      setProjects(refreshed);
      toast({ title: "Projects updated", description: "Changes were saved successfully." });
      setShowManageProjects(false);
    } catch (err: any) {
      setManageError(err?.message || "Failed to save project changes.");
    } finally {
      setSavingProjects(false);
    }
  }, [editableProjects, manageEdits, reloadProjects, setProjects, toast, updateProject, userHeaders]);

  const handleConfirmDeleteProject = React.useCallback(async () => {
    if (!confirmDeleteProject || !userHeaders) return;
    setDeletingProject(true);
    try {
      await deleteProject(confirmDeleteProject.id, userHeaders);
      setProjects((prev) => prev.filter((p) => p.id !== confirmDeleteProject.id));
      const refreshed = await reloadProjects();
      setProjects(refreshed);
      toast({ title: "Project deleted", description: `"${confirmDeleteProject.name}" has been deleted.` });
    } catch (err: any) {
      toast({ title: "Delete failed", description: err?.message || "Could not delete project.", variant: "destructive" });
    } finally {
      setDeletingProject(false);
      setConfirmDeleteProject(null);
    }
  }, [confirmDeleteProject, userHeaders, reloadProjects, setProjects, toast]);

  const handleCreateProjectSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!userHeaders) {
        toast({ title: "Not signed in", description: "Sign in to create a project.", variant: "destructive" });
        return;
      }
      const trimmedName = newProjectName.trim();
      if (!trimmedName) {
        setCreateProjectError("Project name is required.");
        return;
      }
      const trimmedClient = newProjectClient.trim();
      if (!trimmedClient) {
        setCreateProjectError("Client name is required.");
        return;
      }
      const trimmedCity = newProjectCity.trim();
      if (!trimmedCity) {
        setCreateProjectError("City is required.");
        return;
      }
      const normalizedState = newProjectState.trim().toUpperCase();
      if (!normalizedState || !US_STATE_CODES.includes(normalizedState)) {
        setCreateProjectError("State is required (use a two-letter US code).");
        return;
      }
      if (!resolvedCompanyId) {
        setCreateProjectError("No company membership found.");
        return;
      }
      const payload: CreateProjectInput = {
        project_name: trimmedName,
        client_name: trimmedClient,
        city: trimmedCity,
        state: normalizedState,
        company_id: resolvedCompanyId,
      };

      setCreatingProject(true);
      setCreateProjectError(null);
      try {
        persistLastState(normalizedState);
        const project = await createProject(payload, userHeaders);
        toast({
          title: "Project created",
          description: `${project.project_name}${project.client_name ? ` • ${project.client_name}` : ""}`,
        });
        // Upsert selected members (including creator) using RBAC-native project role templates.
        const creatorId = user?.id ?? "";
        const entries = Object.entries(memberAssignments).filter(([, a]) => Boolean(a?.project_role_template));
        if (creatorId && !entries.some(([id]) => id === creatorId)) {
          // Creator is always in the project; default to admin if missing.
          entries.unshift([creatorId, { project_role_template: "ProjectManagers" as const }]);
        }
        if (entries.length > 0) {
          const failures: Array<{ user_id: string; message: string }> = [];
          for (const [userId, assignment] of entries) {
            try {
              await addProjectMember(
                project.id,
                {
                  user_id: userId,
                  project_role_template: assignment.project_role_template ?? undefined,
                },
                userHeaders,
              );
            } catch (err: any) {
              failures.push({ user_id: userId, message: err?.message || "Failed to add" });
            }
          }
          if (failures.length > 0) {
            const first = failures[0];
            toast({
              title: "Some members were not added",
              description: `${failures.length} failed, incl. ${first.user_id}: ${first.message}`,
              variant: "destructive",
            });
          } else {
            toast({
              title: "Members added",
              description: `${entries.length} teammate${entries.length === 1 ? "" : "s"} added to the project.`,
            });
          }
        }
        try {
          setLoadingProjects(true);
          const projectData = await reloadProjects();
          setProjects(projectData);
        } catch (reloadError) {
          console.error("[SidebarNav] failed to refresh projects after creation", reloadError);
        } finally {
          setLoadingProjects(false);
        }
        window.dispatchEvent(new CustomEvent("workspace:project-created", { detail: { project } }));
        resetCreateProjectForm();
      } catch (error: any) {
        console.error("[SidebarNav] create project error", error);
        setCreateProjectError(error?.message || "Failed to create project");
      } finally {
        setCreatingProject(false);
      }
    },
    [
      userHeaders,
      newProjectName,
      newProjectClient,
      newProjectCity,
      newProjectState,
      reloadProjects,
      resetCreateProjectForm,
      resolvedCompanyId,
      memberAssignments,
      persistLastState,
      user?.id,
    ],
  );

  return (

  <div data-tutorial="sidebar" className="min-h-full pb-8">
    {/* Control Panel */}
      <SidebarGroup data-tutorial="sidebar_essentials">
        <SidebarGroupLabel
          className="
            px-4 pt-2 pb-4
            text-[14px]
            font-semibold
            tracking-[0.18em]
            uppercase
            text-purple-400
            justify-center
          "
        >
          Tools
        </SidebarGroupLabel>

        <SidebarSeparator />

        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={() => emitOpenTab("chat")} tooltip="Chat">
                <MessageSquare />
                <span className="text-[14px]">Chat</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={() => emitOpenTab("notes")} tooltip="Notes">
                <Notebook />
                <span className="text-[14px]">Notes</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={() => emitOpenTab("tasks")} tooltip="Tasks">
                <ListTodo />
                <span className="text-[14px]">Tasks</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={() => emitOpenTab("tracker")} tooltip="Project Tracker">
                <LayoutDashboard />
                <span className="text-[14px]">Project Tracker</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={() => emitOpenTab("owner-requirements")} tooltip="Owner Requirements">
                <ClipboardList />
                <span className="text-[14px]">Owner Requirements</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <SidebarMenuItem>
                    <SidebarMenuButton className="opacity-40 cursor-not-allowed" tooltip="">
                      <Lock className="h-4 w-4" />
                      <span className="text-[14px]">Specifications</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>This tool is not currently available.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <SidebarMenuItem>
                    <SidebarMenuButton className="opacity-40 cursor-not-allowed" tooltip="">
                      <Lock className="h-4 w-4" />
                      <span className="text-[14px]">Timesheets</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>This tool is not currently available.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <SidebarMenuItem>
                    <SidebarMenuButton className="opacity-40 cursor-not-allowed" tooltip="">
                      <Lock className="h-4 w-4" />
                      <span className="text-[14px]">Site Photos</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>This tool is not currently available.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      <SidebarSeparator />

      {/* Calculator Section */}
      <SidebarGroup data-tutorial="sidebar_calculators" className="pb-4">
        <Collapsible open={isCalculatorsOpen} onOpenChange={setIsCalculatorsOpen}>
          <SidebarGroupLabel
            className="
              flex flex-col
              px-4
              text-[14px]
              font-semibold
              tracking-[0.18em]
              uppercase
              text-purple-400
            "
          >
            <span>Calculators</span>
            <span className="text-[11px] text-purple-400/60 normal-case tracking-normal">
              Under construction
            </span>
          </SidebarGroupLabel>
          <CollapsibleContent asChild>
            <SidebarGroupContent className="data-[state=closed]:hidden">
              <SidebarMenu>
                {/* <SidebarMenuItem>
                  <SidebarMenuButton onClick={() => emitOpenTab("custom")} tooltip="Buld your own calculators!">
                    <Calculator />
                    <span>Custom Calculator Builder</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                 */}
              </SidebarMenu>
            </SidebarGroupContent>
          </CollapsibleContent>
        </Collapsible>
      </SidebarGroup>

      <SidebarSeparator />

      {/* Project Section */}
      <SidebarGroup data-tutorial="projects">
        <Collapsible open={isProjectsOpen} onOpenChange={setIsProjectsOpen}>
            <SidebarGroupLabel
              className="
              px-4 pt-2 pb-4
              text-[14px]
              font-semibold
              tracking-[0.18em]
              uppercase
              text-purple-400
              justify-center
            "
            >
              Company Projects
            </SidebarGroupLabel>
          <CollapsibleContent asChild>
            <SidebarGroupContent className="data-[state=closed]:hidden">
              <div className="px-2 pt-1 mb-3 flex justify-between items-center gap-2">
                {/* Create Project Button (hidden if user lacks projects:create permission) */}
                {canCreateProjects && (
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateProjectDialog(true);
                    setCreateProjectError(null);
                  }}
                  className={`
                    relative w-full
                    rounded-xl
                    px-3 py-2
                    text-[14px] font-semibold
                    tracking-wide
                    text-white

                    /* Base — solid, no gradient */
                    bg-[#2b1750]

                    /* Border */
                    border border-[#7b2ee6]/60

                    /* Idle glow */
                    shadow-[0_0_10px_rgba(123,46,230,0.35)]

                    transition-all duration-150

                    /* Hover */
                    hover:bg-[#341c63]
                    hover:border-[#a970ff]
                    hover:shadow-[0_0_18px_rgba(123,46,230,0.65)]
                    hover:-translate-y-[1px]

                    /* Press */
                    active:translate-y-0
                    active:bg-[#24123f]
                    active:shadow-[0_0_8px_rgba(123,46,230,0.9)_inset]

                    /* Disabled */
                    disabled:opacity-40
                    disabled:hover:translate-y-0
                    disabled:hover:shadow-none
                  `}
                >
                  Create Project
                </button>
                )}

                {/* Edit Project Button (hidden if user lacks projects:update permission) */}
                {canManageProjects && (
                <button
                  type="button"
                  className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded border border-border bg-background text-muted-foreground hover:text-foreground disabled:opacity-50"
                  title="Manage projects (company admin only)"
                  onClick={() => setShowManageProjects(true)}
                  disabled={loadingProjects || loadingManageAccess}
                >
                  <Pencil className="h-5 w-5" />
                </button>
                )}
              </div>

              {loadingProjects ? (
                <div className="px-2 py-2 text-xs text-muted-foreground">Loading projects…</div>
              ) : projectError ? (
                <div className="px-2 py-2 text-xs text-destructive">{projectError}</div>
              ) : !hasProjectData ? (
                <div className="px-2 py-2 text-xs text-muted-foreground">No projects available yet.</div>
              ) : (
                <SidebarMenu>
                  {clientKeys.map((clientKey) => {
                    const locationMap = projectsByClient.get(clientKey);
                    if (!locationMap) return null;

                    const locationEntries = Array.from(locationMap.entries()).sort((a, b) =>
                      a[1].label.localeCompare(b[1].label),
                    );
                    if (locationEntries.length === 0) return null;

                    // For projects with no client, render grouped by location directly
                    if (clientKey === NO_CLIENT_KEY) {
                      return locationEntries.map(([locationKey, group]) => (
                        <SidebarMenuItem key={`${clientKey}-${locationKey}`}>
                          <Collapsible defaultOpen={false}>
                            <CollapsibleTrigger asChild>
                              <div className="group flex w-full items-center justify-between gap-2 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground cursor-pointer">
                                <div className="flex items-center gap-2">
                                  <Folder className="h-3.5 w-3.5 opacity-80" />
                                  <span className="truncate">{group.label}</span>
                                </div>
                                <ChevronDown className="h-3.5 w-3.5 opacity-80 transition-transform group-data-[state=open]:rotate-180" />
                              </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <div className="mt-1 space-y-1 pl-4">
                                {group.projects.map((proj) => (
                                  <Collapsible key={proj.id} defaultOpen={false}>
                                    <CollapsibleTrigger asChild>
                                      <SidebarMenuButton className="group flex items-start justify-between gap-2 h-auto py-1.5">
                                        <div className="flex items-start gap-2 min-w-0">
                                          <Folder className="h-3.5 w-3.5 opacity-80 shrink-0 mt-0.5" />
                                          <TruncatingProjectTitle title={proj.project_name} />
                                        </div>
                                        <ChevronDown className="h-3.5 w-3.5 opacity-80 transition-transform group-data-[state=open]:rotate-180" />
                                      </SidebarMenuButton>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                      <div className="ml-6 mt-1 space-y-1">
                                        <SidebarMenuButton
                                          onClick={() =>
                                            emitOpenTab("notes", {
                                              projectId: proj.id,
                                              projectName: proj.project_name,
                                              clientName: proj.client_name ?? null,
                                            })
                                          }
                                          tooltip="Notes"
                                        >
                                          <Notebook />
                                          <span>Notes</span>
                                        </SidebarMenuButton>
                                        <SidebarMenuButton
                                          onClick={() =>
                                            emitOpenTab("tasks", {
                                              projectId: proj.id,
                                              projectName: proj.project_name,
                                              clientName: proj.client_name ?? null,
                                            })
                                          }
                                          tooltip="Tasks"
                                        >
                                          <ListTodo />
                                          <span>Tasks</span>
                                        </SidebarMenuButton>
                                        <SidebarMenuButton
                                          onClick={() =>
                                            emitOpenTab("tracker", {
                                              projectId: proj.id,
                                              projectName: proj.project_name,
                                              clientName: proj.client_name ?? null,
                                            })
                                          }
                                          tooltip="Tracker"
                                        >
                                          <LayoutDashboard />
                                          <span>Tracker</span>
                                        </SidebarMenuButton>
                                        {(proj.capabilities?.project_memberships_read === true || 
                                          proj.capabilities?.project_memberships_manage === true) && (
                                          <SidebarMenuButton
                                            onClick={() => openProjectMembers(proj)}
                                            tooltip="Members"
                                          >
                                            <Users />
                                            <span>Members</span>
                                          </SidebarMenuButton>
                                        )}
                                      </div>
                                    </CollapsibleContent>
                                  </Collapsible>
                                ))}
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        </SidebarMenuItem>
                      ));
                    }

                    const displayName = clientKey;
                    return (
                      <SidebarMenuItem key={clientKey}>
                        <Collapsible defaultOpen={false}>
                          <CollapsibleTrigger asChild>
                            <div className="group flex w-full items-center justify-between gap-2 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground cursor-pointer">
                              <div className="flex items-center gap-2">
                                <Folder className="h-3.5 w-3.5 opacity-80" />
                                <span className="truncate">{displayName}</span>
                              </div>
                              <ChevronDown className="h-3.5 w-3.5 opacity-80 transition-transform group-data-[state=open]:rotate-180" />
                            </div>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="mt-1 space-y-2 pl-4">
                              {locationEntries.map(([locationKey, group]) => (
                                <Collapsible key={`${clientKey}-${locationKey}`} defaultOpen={false}>
                                  <CollapsibleTrigger asChild>
                                    <div className="group flex w-full items-center justify-between gap-2 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground cursor-pointer">
                                      <div className="flex items-center gap-2">
                                        <Folder className="h-3.5 w-3.5 opacity-80" />
                                        <span className="truncate">{group.label}</span>
                                      </div>
                                      <ChevronDown className="h-3.5 w-3.5 opacity-80 transition-transform group-data-[state=open]:rotate-180" />

                                    </div>
                                  </CollapsibleTrigger>
                                  <CollapsibleContent>
                                    <div className="mt-1 space-y-1 pl-4">
                                      {group.projects.map((proj) => (
                                        <Collapsible key={proj.id} defaultOpen={false}>
                                          <CollapsibleTrigger asChild>
                                            <SidebarMenuButton className="group flex items-start justify-between gap-2 h-auto py-1.5">
                                              <div className="flex items-start gap-2 min-w-0">
                                                <Folder className="h-3.5 w-3.5 opacity-80 shrink-0 mt-0.5" />
                                                <TruncatingProjectTitle
                                                  title={proj.project_name}
                                                  subtitle={proj.client_name ?? undefined}
                                                />
                                              </div>
                                              <ChevronDown className="h-3.5 w-3.5 opacity-80 transition-transform group-data-[state=open]:rotate-180" />
                                            </SidebarMenuButton>
                                          </CollapsibleTrigger>
                                          <CollapsibleContent>
                                            <div className="ml-6 mt-1 space-y-1">
                                              <SidebarMenuButton
                                                onClick={() =>
                                                  emitOpenTab("notes", {
                                                    projectId: proj.id,
                                                    projectName: proj.project_name,
                                                    clientName: proj.client_name ?? null,
                                                  })
                                                }
                                                tooltip="Notes"
                                              >
                                                <Notebook />
                                                <span>Notes</span>
                                              </SidebarMenuButton>
                                              <SidebarMenuButton
                                                onClick={() =>
                                                  emitOpenTab("tasks", {
                                                    projectId: proj.id,
                                                    projectName: proj.project_name,
                                                    clientName: proj.client_name ?? null,
                                                  })
                                                }
                                                tooltip="Tasks"
                                              >
                                                <ListTodo />
                                                <span>Tasks</span>
                                              </SidebarMenuButton>
                                              <SidebarMenuButton
                                                onClick={() =>
                                                  emitOpenTab("tracker", {
                                                    projectId: proj.id,
                                                    projectName: proj.project_name,
                                                    clientName: proj.client_name ?? null,
                                                  })
                                                }
                                                tooltip="Tracker"
                                              >
                                                <LayoutDashboard />
                                                <span>Tracker</span>
                                              </SidebarMenuButton>
                                              {(proj.capabilities?.project_memberships_read === true || 
                                                proj.capabilities?.project_memberships_manage === true) && (
                                                <SidebarMenuButton
                                                  onClick={() => openProjectMembers(proj)}
                                                  tooltip="Members"
                                                >
                                                  <Users />
                                                  <span>Members</span>
                                                </SidebarMenuButton>
                                              )}
                                            </div>
                                          </CollapsibleContent>
                                        </Collapsible>
                                      ))}
                                    </div>
                                  </CollapsibleContent>
                                </Collapsible>
                              ))}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              )}
            </SidebarGroupContent>
          </CollapsibleContent>
        </Collapsible>
      </SidebarGroup>
      

      {/* Misc Section */}
      {miscProject && (
        <>
          <SidebarSeparator />
          <SidebarGroup>
            <Collapsible defaultOpen={false}>
              <CollapsibleTrigger asChild>
                <SidebarGroupLabel className="cursor-pointer select-none">
                  <Inbox className="mr-1 h-4 w-4" />
                  Misc
                  <ChevronDown className="ml-auto h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
                </SidebarGroupLabel>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() =>
                          emitOpenTab("notes", {
                            projectId: miscProject.id,
                            projectName: "Misc",
                            clientName: null,
                            isMisc: true,
                          })
                        }
                      >
                        <Notebook className="mr-1 h-4 w-4" />
                        Notes
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() =>
                          emitOpenTab("tasks", {
                            projectId: miscProject.id,
                            projectName: "Misc",
                            clientName: null,
                            isMisc: true,
                          })
                        }
                      >
                        <ListTodo className="mr-1 h-4 w-4" />
                        Tasks
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </Collapsible>
          </SidebarGroup>
        </>
      )}

      {/* Project Create Button */}
      <Dialog
        open={showCreateProjectDialog}
        onOpenChange={(open) => {
          if (!open) {
            resetCreateProjectForm();
          } else {
            setShowCreateProjectDialog(true);
          }
        }}
      >
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Create project</DialogTitle>
            <DialogDescription>
              Create a new project and optionally assign teammates.
            </DialogDescription>
          </DialogHeader>

          {!userHeaders ? (
            <div className="text-sm text-destructive">Sign in to create a project.</div>
          ) : null}
          {companyError ? <div className="text-sm text-destructive">{companyError}</div> : null}
          {createProjectError ? <div className="text-sm text-destructive">{createProjectError}</div> : null}

          <form onSubmit={handleCreateProjectSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Company <span className="text-destructive">*</span>
                </label>
                {loadingCompanies ? (
                  <div className="text-sm text-muted-foreground">Loading company…</div>
                ) : (
                  <div className="rounded border border-border bg-muted px-2 py-2 text-sm">
                    {resolvedCompanyName || "—"}
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Project Name <span className="text-destructive">*</span>
                </label>
                <input
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  className="w-full rounded border border-border bg-background px-2 py-2 text-sm"
                  placeholder="e.g. Headquarters Renovation"
                  autoFocus
                  disabled={creatingProject || !userHeaders}
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Client <span className="text-destructive">*</span>
                </label>
                <input
                  value={newProjectClient}
                  onChange={(e) => {
                    setNewProjectClient(e.target.value);
                    setCreateProjectError(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter") return;
                    if (!shouldShowClientSuggestions || clientSuggestions.length === 0) return;
                    event.preventDefault();
                    applyClientSuggestion(clientSuggestions[0].label);
                  }}
                  className="w-full rounded border border-border bg-background px-2 py-2 text-sm"
                  placeholder="Client name (used for grouping)"
                  disabled={creatingProject || !userHeaders}
                />
                {shouldShowClientSuggestions ? (
                  <div className="mt-1 max-h-40 overflow-y-auto rounded border border-border bg-popover p-1">
                    {clientSuggestions.map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        className="block w-full rounded px-2 py-1 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                        onClick={() => applyClientSuggestion(option.label)}
                        disabled={creatingProject || !userHeaders}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="space-y-1">
                <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  City <span className="text-destructive">*</span>
                </label>
                <input
                  value={newProjectCity}
                  onChange={(e) => {
                    setNewProjectCity(e.target.value);
                    setCreateProjectError(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter") return;
                    if (!shouldShowLocationSuggestions || locationSuggestions.length === 0) return;
                    event.preventDefault();
                    applyLocationSuggestion(locationSuggestions[0]);
                  }}
                  className="w-full rounded border border-border bg-background px-2 py-2 text-sm"
                  placeholder="City"
                  disabled={creatingProject || !userHeaders}
                />
                {shouldShowLocationSuggestions ? (
                  <div className="mt-1 max-h-40 overflow-y-auto rounded border border-border bg-popover p-1">
                    {locationSuggestions.map((location) => (
                      <button
                        key={location.key}
                        type="button"
                        className="block w-full rounded px-2 py-1 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                        onClick={() => applyLocationSuggestion(location)}
                        disabled={creatingProject || !userHeaders}
                      >
                        {location.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="space-y-1">
                <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  State <span className="text-destructive">*</span>
                </label>
                <Popover open={statePickerOpen} onOpenChange={setStatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={statePickerOpen}
                      className="w-full justify-between"
                      disabled={creatingProject || !userHeaders}
                      type="button"
                    >
                      <span className="truncate">{newProjectState || "Select state"}</span>
                      <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search state..." />
                      <CommandList>
                        <CommandGroup>
                          {US_STATE_CODES.map((code) => {
                            const isSelected = newProjectState === code;
                            return (
                              <CommandItem
                                key={code}
                                value={code}
                                onSelect={(val) => {
                                  const normalized = val.toUpperCase();
                                  if (!US_STATE_CODES.includes(normalized)) return;
                                  setNewProjectState(normalized);
                                  setStatePickerOpen(false);
                                  setCreateProjectError(null);
                                  persistLastState(normalized);
                                }}
                              >
                                <Check className={`mr-2 h-4 w-4 ${isSelected ? "opacity-100" : "opacity-0"}`} />
                                <span>{code}</span>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <p className="text-[11px] text-muted-foreground">Use the two-letter US state code.</p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-end justify-between gap-3">
                <div>
                  <div className="text-sm font-medium">Members</div>
                </div>
                <input
                  className="w-72 rounded border border-border bg-background px-2 py-2 text-sm"
                  placeholder="Search name or email"
                  value={memberFilter}
                  onChange={(e) => setMemberFilter(e.target.value)}
                  disabled={creatingProject || !userHeaders}
                />
              </div>

              {loadingCompanyMembers ? (
                <div className="text-sm text-muted-foreground">Loading members…</div>
              ) : companyMembersError ? (
                <div className="text-sm text-destructive">{companyMembersError}</div>
              ) : !resolvedCompanyId ? (
                <div className="text-sm text-muted-foreground">Join a company to assign members.</div>
              ) : companyMembers.length === 0 ? (
                <div className="text-sm text-muted-foreground">No company members found.</div>
              ) : filteredCompanyMembers.length === 0 ? (
                <div className="text-sm text-muted-foreground">No members match your search.</div>
              ) : (
                <div className="max-h-[45vh] overflow-auto rounded border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr className="[&>th]:px-3 [&>th]:py-2 text-left">
                        <th className="w-2/5">Name</th>
                        <th className="w-2/5">Email</th>
                        <th className="w-1/5">Assign role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCompanyMembers.map((m) => {
                        const isCreator = Boolean(creatorUserId) && m.user_id === creatorUserId;
                        const email = (m?.profile?.email || "").trim() || "—";
                        const displayNameFromProfile = (m?.profile?.display_name || "").trim();
                        const displayNameFromEmail = email !== "—" ? (email.split("@")[0] || "").trim() : "";
                        const displayName =
                          displayNameFromProfile || (isCreator ? (user?.fullName || "You") : displayNameFromEmail || "Member");
                        const assignment = memberAssignments[m.user_id] ?? { project_role_template: null };
                        const roleValue = assignment.project_role_template ?? "";
                        return (
                          <tr key={m.user_id} className="border-t [&>td]:px-3 [&>td]:py-2 align-top">
                            <td className="font-medium">
                              <div className="flex items-center gap-2">
                                <span className="truncate">{displayName}</span>
                                {isCreator ? (
                                  <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">
                                    Creator
                                  </span>
                                ) : null}
                              </div>
                            </td>
                            <td className="text-muted-foreground">{email}</td>
                            <td>
                              <div className="space-y-1">
                                <select
                                  className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
                                  value={roleValue}
                                  onChange={(e) => {
                                    const v = e.target.value as "" | "ProjectManagers" | "User";
                                    setAssignmentRole(m.user_id, v ? v : null);
                                  }}
                                  disabled={creatingProject || !userHeaders}
                                >
                                  <option value="" disabled={isCreator}>
                                    Not in project
                                  </option>
                                  <option value="ProjectManagers">project manager</option>
                                  <option value="User">member</option>
                                </select>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <DialogFooter>
            <Button
              variant="outline"
              type="button"
              onClick={resetCreateProjectForm}
              disabled={creatingProject}
              className="text-[16px] px-5 py-2.5 border-white/10 bg-black/30 hover:bg-white/5"
            >
              Cancel
            </Button>
              <Button
                type="submit"
                disabled={creatingProject || !hasCompany || !userHeaders}
                className={`
                  px-6 py-2.5
                  text-[16px] font-semibold tracking-[0.2px]
                  rounded-xl

                  bg-gradient-to-b from-[#7b2ee6] to-[#5a1fc7]
                  text-white

                  border border-[#9b5cff]/40

                  shadow-[0_8px_26px_rgba(123,46,230,0.35),inset_0_1px_0_rgba(255,255,255,0.15)]

                  transition-all duration-150 ease-out

                  hover:from-[#8c44ff]
                  hover:to-[#6b2cff]
                  hover:shadow-[0_0_22px_rgba(123,46,230,0.55),0_8px_30px_rgba(0,0,0,0.6)]
                  hover:-translate-y-[1px]

                  active:translate-y-[0px]
                  active:shadow-[inset_0_3px_10px_rgba(0,0,0,0.55)]

                  disabled:opacity-40
                  disabled:hover:translate-y-0
                  disabled:hover:shadow-none
                `}
              >
                {creatingProject ? "Creating…" : "Create project"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Project Edit Button */}
      <Dialog open={showManageProjects} onOpenChange={setShowManageProjects}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Manage projects</DialogTitle>
            <DialogDescription>Edit name, client, city, and state for projects where you are a project admin.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>
              Showing {filteredManageProjects.length} of {editableProjects.length} projects
            </span>
            <input
              className="w-64 rounded border border-border bg-background px-2 py-1 text-sm"
              placeholder="Filter by name, client, city, or state"
              value={manageFilter}
              onChange={(e) => setManageFilter(e.target.value)}
              disabled={savingProjects}
            />
          </div>
          {manageError ? <div className="text-sm text-destructive">{manageError}</div> : null}
          {editableProjects.length === 0 ? (
            <div className="text-sm text-muted-foreground">You don’t have admin access to any projects.</div>
          ) : filteredManageProjects.length === 0 ? (
            <div className="text-sm text-muted-foreground">No projects match your filter.</div>
          ) : (
            <div className="max-h-[70vh] overflow-auto rounded border">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr className="[&>th]:px-3 [&>th]:py-2 text-left">
                    <th className="w-1/4">Project</th>
                    <th className="w-1/4">Client</th>
                    <th className="w-1/4">City</th>
                    <th className="w-1/6">State</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {filteredManageProjects.map((proj) => {
                    const row = manageEdits[proj.id] ?? { project_name: "", client_name: "", city: "", state: "" };
                    return (
                      <tr key={proj.id} className="border-t [&>td]:px-3 [&>td]:py-2 align-top">
                        <td>
                          <input
                            className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
                            value={row.project_name}
                            onChange={(e) => handleManageFieldChange(proj.id, "project_name", e.target.value)}
                            placeholder="Project name"
                            disabled={savingProjects}
                          />
                        </td>
                        <td>
                          <input
                            className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
                            value={row.client_name}
                            onChange={(e) => handleManageFieldChange(proj.id, "client_name", e.target.value)}
                            placeholder="Client"
                            disabled={savingProjects}
                          />
                        </td>
                        <td>
                          <input
                            className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
                            value={row.city}
                            onChange={(e) => handleManageFieldChange(proj.id, "city", e.target.value)}
                            placeholder="City"
                            disabled={savingProjects}
                          />
                        </td>
                        <td>
                          <Popover
                            open={!!manageStatePickerOpen[proj.id]}
                            onOpenChange={(open) =>
                              setManageStatePickerOpen((prev) => ({ ...prev, [proj.id]: open }))
                            }
                          >
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={!!manageStatePickerOpen[proj.id]}
                                className="w-full justify-between h-[30px] px-2 py-1"
                                disabled={savingProjects}
                                type="button"
                              >
                                <span className="truncate">{row.state || "Select state"}</span>
                                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="p-0" align="start">
                              <Command>
                                <CommandInput placeholder="Search state..." />
                                <CommandList>
                                  <CommandGroup>
                                    {US_STATE_CODES.map((code) => {
                                      const isSelected = row.state === code;
                                      return (
                                        <CommandItem
                                          key={code}
                                          value={code}
                                          onSelect={(val) => {
                                            const normalized = val.toUpperCase();
                                            if (!US_STATE_CODES.includes(normalized)) return;
                                            handleManageFieldChange(proj.id, "state", normalized);
                                            setManageStatePickerOpen((prev) => ({ ...prev, [proj.id]: false }));
                                          }}
                                        >
                                          <Check className={`mr-2 h-4 w-4 ${isSelected ? "opacity-100" : "opacity-0"}`} />
                                          {code}
                                        </CommandItem>
                                      );
                                    })}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </td>
                        <td className="text-center">
                          {(proj.project_name || "").trim().toLowerCase() !== "misc" && (
                            <button
                              type="button"
                              onClick={() => setConfirmDeleteProject({ id: proj.id, name: proj.project_name || "Untitled" })}
                              disabled={savingProjects || deletingProject}
                              className="p-0.5 rounded hover:text-red-800 transition disabled:opacity-50"
                            >
                              <Trash2 size={15} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManageProjects(false)} disabled={savingProjects}>
              Cancel
            </Button>
            <Button onClick={handleSaveManageProjects} disabled={savingProjects || editableProjects.length === 0}>
              {savingProjects ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDeleteProject} onOpenChange={(open) => { if (!open) setConfirmDeleteProject(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{confirmDeleteProject?.name}&rdquo;? Notes and tasks will be preserved but unlinked from this project.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingProject}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteProject}
              disabled={deletingProject}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingProject ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Project Members */}
      <Dialog
        open={showProjectMembersDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowProjectMembersDialog(false);
            setMembersProject(null);
            setMembersError(null);
            setMembersCompanyMembers([]);
            setMembersProjectMembers([]);
            setMembersAssignments({});
            setMembersInitialAssignments({});
            setMembersFilter("");
            setMembersLoading(false);
            setMembersSaving(false);
          } else {
            setShowProjectMembersDialog(true);
          }
        }}
      >
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Project members</DialogTitle>
            <DialogDescription>
              {membersProject ? (
                <>
                  Manage membership for <span className="font-medium">{membersProject.project_name}</span>.
                </>
              ) : (
                "Manage project membership."
              )}
            </DialogDescription>
          </DialogHeader>

          {membersError ? <div className="text-sm text-destructive">{membersError}</div> : null}

          {!membersProject ? (
            <div className="text-sm text-muted-foreground">No project selected.</div>
          ) : membersLoading ? (
            <div className="text-sm text-muted-foreground">Loading members…</div>
          ) : membersCompanyMembers.length === 0 ? (
            <div className="text-sm text-muted-foreground">No company members found for this project.</div>
          ) : (
            <>
              {!canEditMembersForProject(membersProject) ? (
                <div className="rounded border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                  You can view members, but only project/company admins can edit membership.
                </div>
              ) : null}

              <div className="flex items-end justify-between gap-3">
                <div>
                  <div className="text-sm font-medium">Members</div>
                </div>
                <input
                  className="w-72 rounded border border-border bg-background px-2 py-2 text-sm"
                  placeholder="Search name or email"
                  value={membersFilter}
                  onChange={(e) => setMembersFilter(e.target.value)}
                  disabled={!userHeaders || membersSaving}
                />
              </div>

              {filteredMembersCompanyMembers.length === 0 ? (
                <div className="text-sm text-muted-foreground">No members match your search.</div>
              ) : (
                <div className="max-h-[60vh] overflow-auto rounded border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr className="[&>th]:px-3 [&>th]:py-2 text-left">
                        <th className="w-2/5">Name</th>
                        <th className="w-2/5">Email</th>
                        <th className="w-1/5">Assign role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMembersCompanyMembers.map((m) => {
                        const isMe = Boolean(user?.id) && m.user_id === user?.id;
                        const email = (m?.profile?.email || "").trim() || "—";
                        const displayNameFromProfile = (m?.profile?.display_name || "").trim();
                        const displayNameFromEmail = email !== "—" ? (email.split("@")[0] || "").trim() : "";
                        const displayName = displayNameFromProfile || (isMe ? (user?.fullName || "You") : displayNameFromEmail || "Member");
                        const assignment = membersAssignments[m.user_id] ?? { project_role_template: null };
                        const roleValue = assignment.project_role_template ?? "";
                        const canEdit = userHeaders && canEditMembersForProject(membersProject) && !membersSaving;
                        return (
                          <tr key={m.user_id} className="border-t [&>td]:px-3 [&>td]:py-2 align-top">
                            <td className="font-medium">
                              <div className="flex items-center gap-2">
                                <span className="truncate">{displayName}</span>
                                {isMe ? (
                                  <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">
                                    {user?.firstName || "You"}
                                  </span>
                                ) : null}
                              </div>
                            </td>
                            <td className="text-muted-foreground">{email}</td>
                            <td>
                              <div className="space-y-1">
                                <select
                                  className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
                                  value={roleValue}
                                  onChange={(e) => {
                                    const v = e.target.value as "" | "ProjectManagers" | "User";
                                    setMembersAssignmentRole(m.user_id, v ? v : null);
                                  }}
                                  disabled={!canEdit}
                                >
                                  <option value="">Not in project</option>
                                  <option value="ProjectManagers">project manager</option>
                                  <option value="User">member</option>
                                </select>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              type="button"
              onClick={() => setShowProjectMembersDialog(false)}
              disabled={membersSaving}
            >
              Close
            </Button>
            <Button
              type="button"
              onClick={handleSaveProjectMembers}
              disabled={!membersProject || !userHeaders || membersSaving || !canEditMembersForProject(membersProject)}
              title={
                membersProject && !canEditMembersForProject(membersProject)
                  ? "Only project/company admins can edit members"
                  : undefined
              }
            >
              {membersSaving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    
  );
}
